// Smart Daara - AI tutor chat (course-scoped, students only)
import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { aiAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sparkles, Send, X } from 'lucide-react';

export const TutorChat = ({ courseId }) => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) aiAPI.tutorHistory(courseId).then((r) => setMessages(r.data)).catch(() => {});
  }, [open, courseId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setBusy(true);
    try {
      const r = await aiAPI.tutorAsk(courseId, { message: msg, language });
      setMessages((m) => [...m, { role: 'model', text: r.data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'model', text: e.response?.data?.detail || (fr ? 'Assistant IA indisponible' : 'AI assistant unavailable') }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="tutor-fab"
        className="fixed bottom-20 right-5 z-50 w-12 h-12 rounded-full bg-emerald-700 text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
      </button>
      {open && (
        <div className="fixed bottom-36 right-5 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col" style={{ height: '60vh' }} data-testid="tutor-panel">
          <div className="px-4 py-3 border-b bg-emerald-700 text-white rounded-t-2xl">
            <p className="font-semibold text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" />{fr ? 'Tuteur IA — Pose ta question sur ce cours' : 'AI Tutor — Ask about this course'}</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 && <p className="text-xs text-slate-400 text-center mt-6">{fr ? 'Je réponds uniquement sur le contenu de ce cours 😊' : 'I only answer about this course 😊'}</p>}
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'ml-auto bg-emerald-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                {m.text}
              </div>
            ))}
            {busy && <div className="bg-slate-100 rounded-xl px-3 py-2 text-sm text-slate-400 w-16">…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={fr ? 'Ta question…' : 'Your question…'} data-testid="tutor-input" />
            <Button size="icon" className="bg-emerald-700" onClick={send} disabled={busy} data-testid="tutor-send"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
};
export default TutorChat;
